/**
 * DashboardPage — /insights
 *
 *   Row 1 — LLM cost · Sherlock usage (last 30 days)
 *   Row 2 — Production health: one ServiceHealthCard per service the Datadog
 *            health sync has data for (p99 / error rate / RPM / trend)
 *   Row 3 — Sentry hotspots (events this week vs last) · What Sherlock learned
 */

import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import type { ReactNode } from 'react';

import { useInsightsDashboard } from '@/hooks/useInsightsDashboard';
import { CostCard } from '@/components/CostCard';
import { InsightCard } from '@/components/InsightCard';
import { ServiceHealthCard } from '@/components/ServiceHealthCard';

import type { LearningStats, SentryHotspot, UsageStats } from '@/types/insights';

const ROW_VARIANTS = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
};
const CARD_VARIANTS = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0 },
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** "+12% vs last week" — more events is worse, so up is red. */
function WeekChange({ now, prev }: { now: number; prev: number }) {
  if (prev === 0) {
    return (
      <Typography variant="caption" sx={{ color: 'error.main' }}>
        new this week
      </Typography>
    );
  }
  const pct = Math.round(((now - prev) / prev) * 100);
  const color = pct > 10 ? 'error.main' : pct < -10 ? 'success.main' : 'text.secondary';
  return (
    <Typography variant="caption" sx={{ color }}>
      {pct > 0 ? '+' : ''}
      {pct}% vs last week
    </Typography>
  );
}

function HotspotRow({ hotspot }: { hotspot: SentryHotspot }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, minHeight: 44 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Link
          component={RouterLink}
          to={`/issues/${hotspot.issue_id}`}
          underline="hover"
          color="text.primary"
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            fontWeight: 500,
          }}
        >
          {hotspot.title}
        </Link>
        <Typography variant="caption" color="text.secondary">
          {hotspot.project}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {hotspot.events_7d.toLocaleString()}
        </Typography>
        <WeekChange now={hotspot.events_7d} prev={hotspot.events_prev_7d} />
      </Box>
    </Box>
  );
}

function Stat({ label, value, detail }: { label: string; value: ReactNode; detail?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Box>
  );
}

function UsagePanel({ usage }: { usage: UsageStats }) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <Stat
          label="Investigations"
          value={usage.investigations}
          detail={`by ${usage.investigators} ${usage.investigators === 1 ? 'person' : 'people'}`}
        />
      </Grid>
      <Grid item xs={6}>
        <Stat
          label="PR reviews"
          value={usage.pr_reviews}
          detail={`${usage.pr_replies} @sherlock replies`}
        />
      </Grid>
      <Grid item xs={6}>
        <Stat
          label="Review findings"
          value={usage.findings_posted}
          detail={`${usage.findings_fixed} fixed · ${usage.findings_dismissed} dismissed`}
        />
      </Grid>
      <Grid item xs={6}>
        <Stat
          label="Investigation feedback"
          value={`${usage.thumbs_up} / ${usage.thumbs_down}`}
          detail="helpful / not helpful"
        />
      </Grid>
    </Grid>
  );
}

function LearningPanel({ learning }: { learning: LearningStats }) {
  const proposed = learning.rules.reduce((n, r) => n + r.proposed, 0);
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4}>
        <Stat label="Investigation episodes" value={learning.investigation_episodes} />
        <Stat label="Review episodes" value={learning.review_episodes} />
      </Stack>
      <Divider />
      <Box>
        <Typography variant="caption" color="text.secondary">
          Learned rules
        </Typography>
        {learning.rules.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No rules synthesized yet.
          </Typography>
        ) : (
          learning.rules.map((r) => (
            <Box
              key={r.domain}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}
            >
              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                {r.domain.replace('_', ' ')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {r.active} active · {r.proposed} proposed
              </Typography>
            </Box>
          ))
        )}
      </Box>
      {proposed > 0 && (
        <Alert severity="info" variant="outlined">
          {proposed} proposed {proposed === 1 ? 'rule is' : 'rules are'} waiting for review. They
          aren&apos;t used until promoted.
        </Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const { data, isLoading, error, refetch } = useInsightsDashboard();

  // --- Error state ---
  if (error && !isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Insights
        </Typography>
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          Failed to load dashboard: {error.message}
        </Alert>
      </Box>
    );
  }

  const services = data?.service_health ?? [];
  const hotspots = data?.sentry_hotspots ?? [];
  const newIssues = data?.sentry_new_issues_7d ?? 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Insights
      </Typography>

      <Box component={motion.div} initial="hidden" animate="show" variants={ROW_VARIANTS}>
        {/* Row 1 — LLM cost · Sherlock usage */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Box component={motion.div} variants={CARD_VARIANTS} sx={{ height: '100%' }}>
              <InsightCard
                title="LLM cost"
                subtitle={
                  data
                    ? `Claude API spend · burn rate $${data.burn_rate_per_day.toFixed(2)} / day this month`
                    : 'Claude API spend'
                }
              >
                <CostCard dashboard={data} isDashboardLoading={isLoading} />
              </InsightCard>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box component={motion.div} variants={CARD_VARIANTS} sx={{ height: '100%' }}>
              <InsightCard
                title="Sherlock usage"
                subtitle={data ? `Last ${data.usage.window_days} days` : undefined}
              >
                {isLoading || !data ? (
                  <Skeleton variant="rounded" height={160} />
                ) : (
                  <UsagePanel usage={data.usage} />
                )}
              </InsightCard>
            </Box>
          </Grid>
        </Grid>
        {/* Row 2 — Production health */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Production health
          </Typography>
          {isLoading ? (
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} md={4} xl={2} key={i}>
                  <Skeleton variant="rounded" height={140} />
                </Grid>
              ))}
            </Grid>
          ) : services.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No service health data yet.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {services.map((s) => (
                <Grid item xs={12} sm={6} md={4} xl={2} key={s.service}>
                  <ServiceHealthCard health={s} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Row 3 — Sentry hotspots · What Sherlock learned */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Box component={motion.div} variants={CARD_VARIANTS} sx={{ height: '100%' }}>
              <InsightCard
                title="Sentry hotspots"
                subtitle={`Unresolved issues by events in the last 7 days · ${newIssues} new ${
                  newIssues === 1 ? 'issue' : 'issues'
                } this week`}
                action={
                  <Button
                    component={RouterLink}
                    to="/issues?status=unresolved"
                    size="small"
                    sx={{ minHeight: 44, minWidth: 44 }}
                  >
                    All issues →
                  </Button>
                }
              >
                {isLoading ? (
                  <Stack spacing={1}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton key={i} variant="text" height={44} />
                    ))}
                  </Stack>
                ) : hotspots.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No unresolved Sentry events in the last 7 days.
                  </Typography>
                ) : (
                  <Stack divider={<Divider flexItem />}>
                    {hotspots.map((h) => (
                      <HotspotRow key={h.issue_id} hotspot={h} />
                    ))}
                  </Stack>
                )}
              </InsightCard>
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box component={motion.div} variants={CARD_VARIANTS} sx={{ height: '100%' }}>
              <InsightCard
                title="What Sherlock learned"
                subtitle="Episodic memory and learned rules"
                action={
                  <Button
                    component={RouterLink}
                    to="/memory"
                    size="small"
                    sx={{ minHeight: 44, minWidth: 44 }}
                  >
                    Memory →
                  </Button>
                }
              >
                {isLoading || !data ? (
                  <Skeleton variant="rounded" height={160} />
                ) : (
                  <LearningPanel learning={data.learning} />
                )}
              </InsightCard>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
